import { useEffect, useRef, useState } from 'react'
import CloseIcon from '../../../assets/CloseIcon'
import ReminderIcon from '../../../assets/ReminderIcon'
import SearchIcon from '../../../assets/SearchIcon'
import { INVENTORY_STAGES, INVENTORY_TABS } from '../../../common/constant/page-constant'
import useCheckMobileScreen from '../../../common/hooks/use-check-mobile-screen-hook'
import { getReminderHelper } from '../../../helper/query-helper'
import { getInventoryDetailsHelper } from '../../../helper/home-helper'

const InventoryFilter = ({
  filterOptions,
  setFilterOptions,
  inventoryStage,
  setInventoryStage,
  activeTab,
  inventoryData,
  onReminderFilter,
  isReminderFilterActive,
  activeReminderDays,
  checkAllBranch,
  onCheckAllBranch,
  showCheckAllBranch,
}) => {
  const [searchTerm, setSearchTerm] = useState(filterOptions?.search || '')
  const [expiringCount, setExpiringCount] = useState(0)
  const [reminderOptions, setReminderOptions] = useState([])
  const [showReminderDropdown, setShowReminderDropdown] = useState(false)
  const isMobile = useCheckMobileScreen()
  const dropdownRef = useRef(null)

  useEffect(() => {
    fetchReminderAndCount()
  }, [activeTab])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowReminderDropdown(false)
      }
    }
    if (showReminderDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showReminderDropdown])

  const fetchReminderAndCount = () => {
    const productType = activeTab === INVENTORY_TABS.PHARAMCEUTICAL
      ? 'PHARMACEUTICAL'
      : 'NON_PHARMACEUTICAL'

    getReminderHelper()
      .then(reminders => {
        const matchingReminders = reminders?.filter(r => r.product_type === productType) || []
        const uniqueDays = [...new Set(matchingReminders.map(r => r.days).filter(Boolean))].sort((a, b) => a - b)

        if (uniqueDays.length === 0) {
          setExpiringCount(0)
          setReminderOptions([])
          return
        }

        const countPromises = uniqueDays.map(days => {
          const params = { type: productType, expiry_days: days, page: 1, limit: 1 }
          return getInventoryDetailsHelper(undefined, params)
            .then(response => ({ days, count: response?.pagination?.count || 0 }))
            .catch(() => ({ days, count: 0 }))
        })

        return Promise.all(countPromises)
      })
      .then(options => {
        if (!options) return
        setReminderOptions(options)
        const totalCount = options.reduce((max, o) => o.days > max.days ? o : max, { days: 0, count: 0 })
        setExpiringCount(totalCount.count)
      })
      .catch(() => {
        setExpiringCount(0)
        setReminderOptions([])
      })
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    setFilterOptions({ search: searchTerm })
  }

  const handleClearSearch = (e) => {
    e.preventDefault()
    if (!searchTerm) return
    setSearchTerm('')
    setFilterOptions({ search: '' })
  }

  const handleReminderClick = () => {
    if (reminderOptions.length > 0) {
      setShowReminderDropdown(!showReminderDropdown)
    }
  }

  const handleSelectReminder = (days) => {
    setShowReminderDropdown(false)
    onReminderFilter(activeReminderDays === days ? 0 : days)
  }

  const handleClearFilter = () => {
    setShowReminderDropdown(false)
    setInventoryStage(INVENTORY_STAGES.PRODUCT_LISTING)
    onReminderFilter(0)
  }

  const renderReminderDropdown = () => {
    if (!showReminderDropdown || reminderOptions.length === 0) return null

    const tabLabel = activeTab === INVENTORY_TABS.PHARAMCEUTICAL
      ? 'Pharmaceutical'
      : 'Non-Pharmaceutical'

    return (
      <div style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '4px',
        backgroundColor: 'white',
        border: '1px solid #ddd',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 100,
        minWidth: '240px',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '10px 16px',
          fontSize: '12px',
          color: '#999',
          borderBottom: '1px solid #f0f0f0',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}>
          {tabLabel} — Expiry Filter
        </div>
        {reminderOptions.map(option => {
          const isActive = activeReminderDays === option.days
          return (
            <div
              key={option.days}
              onClick={() => handleSelectReminder(option.days)}
              style={{
                padding: '10px 16px',
                cursor: 'pointer',
                backgroundColor: isActive ? '#fff3e0' : 'transparent',
                borderLeft: isActive ? '3px solid #f57c00' : '3px solid transparent',
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = '#f5f5f5' }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent' }}
            >
              <div style={{ fontWeight: 500, fontSize: '14px', color: '#333' }}>
                {option.days} days before expiry
              </div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                {option.count} product{option.count !== 1 ? 's' : ''}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <>
      {isMobile && (
        <div className="addProductBtnMobile dFlex flexColumnReverse aiCenter mwebVisible fixed">
          {inventoryStage === INVENTORY_STAGES.PRODUCT_LISTING && (
            <div className="formGroup mb-0 searchBar expand">
              <form onSubmit={handleSearchSubmit}>
                <input
                  type="text"
                  className="formControl"
                  placeholder="Search By Product Name or Generic Name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchSubmit(e)
                    }
                  }}
                />
              </form>
              <div className="searchIcon" onClick={handleSearchSubmit}>
                <SearchIcon />
              </div>
              <div className="closeIcon" onClick={handleClearSearch}>
                <CloseIcon />
              </div>
            </div>
          )}
          {inventoryStage === INVENTORY_STAGES.PRODUCT_LISTING && showCheckAllBranch && (
            <label className="checkAllBranch mb-2">
              <input
                type="checkbox"
                checked={checkAllBranch}
                onChange={(e) => onCheckAllBranch(e.target.checked)}
              />
              <span className="checkmark"></span>
              Check All Branch
            </label>
          )}
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button
              className={`animated zoomIn btn ${isReminderFilterActive ? 'btnPrimary' : 'btnSecondary'} pointer mb-2`}
              onClick={handleReminderClick}
            >
              <ReminderIcon count={expiringCount} />
            </button>
            {renderReminderDropdown()}
          </div>
        </div>
      )}
      {!isMobile && (
        <div className="pageBodyRightPanelHeader dFlex jcSpaceBetween aiStart">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, marginRight: '16px' }}>
            <div className="pageBodyRightPanelHeading">Manage Inventory</div>
            <div className="dFlex aiCenter" style={{ gap: '16px' }}>
              {inventoryStage === INVENTORY_STAGES.PRODUCT_LISTING && (
              <div className="formGroup mb-0 searchBar expand">
                <form onSubmit={handleSearchSubmit}>
                  <input
                    type="text"
                    className="formControl"
                    placeholder="Search By Product Name or Generic Name"
                    style={{ width: '440px', maxWidth: '100%' }}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearchSubmit(e)
                      }
                    }}
                  />
                </form>
                <div className="searchIcon" onClick={handleSearchSubmit}>
                  <SearchIcon />
                </div>
                <div className="closeIcon" onClick={handleClearSearch}>
                  <CloseIcon />
                </div>
              </div>
            )}
            {inventoryStage === INVENTORY_STAGES.PRODUCT_LISTING && showCheckAllBranch && (
              <label className="checkAllBranch">
                <input
                  type="checkbox"
                  checked={checkAllBranch}
                  onChange={(e) => onCheckAllBranch(e.target.checked)}
                />
                <span className="checkmark"></span>
                Check All Branch
              </label>
            )}
            </div>
          </div>
          <div className="addProductBtn dFlex aiCenter">
            <div
              ref={dropdownRef}
              style={{ position: 'relative' }}
              className={`pointer ml-4 ${isReminderFilterActive ? 'reminderActive' : ''}`}
              title={`${expiringCount} products expiring soon`}
            >
              <div onClick={handleReminderClick}>
                <ReminderIcon count={expiringCount} />
              </div>
              {renderReminderDropdown()}
            </div>
            <div
              className="pointer ml-4"
              onClick={handleClearFilter}
            >
              <CloseIcon />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default InventoryFilter
